import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { Link, useLocation } from 'react-router-dom';
import { registryRecordHref } from '../../../components/SharedRecord';
import { useCompanyLaborEntries, useCompanyRateObservations } from './companyEvidenceHooks';
import { VehicleRelationshipsSection } from './VehicleRelationshipsSection';
import { useVehicleRelationships } from './vehicleIdentityHooks';
export function CompanyVehiclesSection({ tenantId, profileId }) {
    const location = useLocation();
    const origin = location.pathname + location.search;
    const holdingsQuery = useVehicleRelationships('', profileId);
    const laborQuery = useCompanyLaborEntries(tenantId, profileId);
    const rateQuery = useCompanyRateObservations(tenantId, profileId);
    const holdings = holdingsQuery.data ?? [];
    const heldVehicleIds = new Set(holdings.map((h)=>h.vehicleId));
    // Mentions: every vehicle referenced by this company's labor/rate evidence
    // that is NOT also a formal holding — a Map so the same vehicle referenced
    // by both a labor entry and a rate observation surfaces once.
    const mentioned = new Map();
    for (const l of laborQuery.data ?? []){
        if (l.contractVehicleId && !heldVehicleIds.has(l.contractVehicleId)) {
            mentioned.set(l.contractVehicleId, l.contractVehicleName || l.contractVehicleId);
        }
    }
    for (const r of rateQuery.data ?? []){
        if (r.contractVehicleId && !heldVehicleIds.has(r.contractVehicleId)) {
            mentioned.set(r.contractVehicleId, r.vehicleName || r.contractVehicleId);
        }
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "space-y-2",
                "data-testid": Testids.deliveryRegistries.companyVehicleHoldingsSection,
                children: /*#__PURE__*/ _jsx(VehicleRelationshipsSection, {
                    companyId: profileId
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                "data-testid": Testids.deliveryRegistries.companyVehicleMentionsSection,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        // specra-allow text-px, arbitrary-length: matches the established Registries section-heading pattern (see CompanyLineageSection.tsx)
                        className: "text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                        children: "Vehicle mentions"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-muted-foreground",
                        children: "Vehicles this company is mentioned against in its LCATs or rate evidence only — not proof of current holding or eligibility. Recorded relationships are listed above."
                    }),
                    laborQuery.isLoading || rateQuery.isLoading ? /*#__PURE__*/ _jsx("output", {
                        children: "Loading source associations…"
                    }) : laborQuery.isError || rateQuery.isError ? /*#__PURE__*/ _jsx("p", {
                        role: "alert",
                        children: "Could not load source associations."
                    }) : mentioned.size === 0 ? /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-muted-foreground",
                        children: "No incidental Vehicle mentions recorded."
                    }) : [
                        ...mentioned.entries()
                    ].map(([vehicleId, name])=>/*#__PURE__*/ _jsx("div", {
                            "data-testid": Testids.deliveryRegistries.companyVehicleMentionRow,
                            className: "rounded-md border border-border/60 p-2 text-[length:var(--fs-ui-caption)] text-muted-foreground",
                            children: /*#__PURE__*/ _jsx(Link, {
                                className: "text-primary underline",
                                to: registryRecordHref('vehicle', vehicleId, origin),
                                children: name
                            })
                        }, vehicleId))
                ]
            })
        ]
    });
}
